/**
 * Three-Plume Visualization Wrapper
 *
 * Combines ThreePlumeMap and ThreePlumeChart into a cohesive visualization
 * with synchronized timeline controls and coupling physics display.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { ThreePlumeMap, WellPlacementMode } from './ThreePlumeMap';
import { ThreePlume3DView } from './ThreePlume3DView';
import { WellInputModal, WellParams } from './WellInputModal';
import { ThreePlumeState, ThreePlumeResponse, RecoveryWell, MDPEWell, SiteGradientMap, polygonApi, AllContaminantBoundariesResponse, ContaminantBoundaryData, isDisplayCompound, DWRecommendation, RemediationHotspot, TimelinePredictionsResponse } from '../../api/polygon';
import { BeakerIcon, MapIcon, PlusIcon, ArrowPathIcon, TrashIcon, CubeIcon, Bars2Icon, ViewColumnsIcon } from '@heroicons/react/24/outline';
import { WellManagementTable } from './WellManagementTable';
import { generateMultiContaminantPlumes, CompoundPlumeData, getCompoundProperties } from './MultiContaminantPlumeUtils';
import { UnifiedTimeSlider } from './UnifiedTimeSlider';
import { RemediationImpactCalculator } from './RemediationImpactCalculator';
import { applyHorizonOverridesToBoundaries } from './horizonOverrideUtils';
import { usePolygonSliderStore } from './polygonSliderStore';

// QC well patterns to filter from sentinel recommendations (matches backend)
const QC_WELL_PATTERNS = ['Dup', 'Blank', 'Composite', 'Stockpile', 'Duplicate'];
function isRealWellName(name: string | null | undefined): boolean {
  if (!name) return true;
  return !QC_WELL_PATTERNS.some(p => name.includes(p));
}

// ============================================================================
// TYPES
// ============================================================================

// Extracted plume boundary from AGMR document
export interface ExtractedPlumeBoundary {
  concentration_ug_l: number;
  boundary_coords: [number, number][]; // Array of [lat, lon]
}

// Extracted well inventory item from AGMR
export interface ExtractedWellInventoryItem {
  well_id: string;
  // 'monitor' is from API, 'monitoring' is frontend standard - both are accepted
  well_type: 'monitoring' | 'monitor' | 'recovery' | 'mdpe' | 'sve' | 'air_sparge' | 'injection' | 'other';
  lat: number;
  lon: number;
  status?: string;
  depth_ft?: number;
  pump_rate_gpm?: number;
}

export interface ThreePlumeVisualizationProps {
  centerLat: number;
  centerLon: number;
  threePlumeResult?: ThreePlumeResponse | null;
  flowDirection?: number;
  siteName?: string;
  siteId?: string;  // For fetching gradient map data from database
  // Directly passed extracted gradient data (takes priority over siteId fetch)
  extractedGradientMap?: SiteGradientMap | null;
  // Extracted plume boundaries for T=0 visualization
  extractedPlumeBoundaries?: ExtractedPlumeBoundary[] | null;
  // Extracted well inventory for auto-populating recovery/MDPE wells
  extractedWellInventory?: ExtractedWellInventoryItem[] | null;
  // Geological parameters for 3D view
  depthToWaterFt?: number;
  aquiferThicknessFt?: number;
  // Wells and re-run support
  onRerunWithWells?: (recoveryWells: RecoveryWell[], mdpeWells: MDPEWell[]) => void;
  isSimulating?: boolean;
  // Progressive loading: show indicator while simulation runs in background
  simulationLoading?: boolean;
  // Pre-fetched data from page-bundle (skips API calls when provided)
  bundledContaminantBoundaries?: ContaminantBoundaryData[] | null;
  bundledGradientMap?: SiteGradientMap | null;
  bundledTimeline?: import('../../api/polygon').TimelineResponse | null;
  bundledSnapshots?: import('../../api/polygon').PlumeSnapshotsResponse | null;
  // Pane mode lets the page render multiple instances, each showing one piece:
  //   'all'        — full layout (default; legacy behavior)
  //   'bars-only'  — only the 5-card summary bar + Contaminants vs MCL legend
  //   '2d-only'    — only the 2D satellite map block
  //   '3d-only'    — only the 3D cross-section block
  paneMode?: 'all' | 'bars-only' | '2d-only' | '3d-only';
  // Controlled visibility — when supplied, the page owns which compounds are
  // visible so the persistent legend at the top can drive both map panes.
  controlledVisibleCompounds?: Set<string>;
  onVisibleCompoundsChange?: (next: Set<string>) => void;
  /** Active regulatory library slug. When supplied, boundary fetches send
   *  ?library_slug=... so plume polygons reflect the chosen MCLs. */
  regLibrary?: string;
}

type ViewMode = 'combined' | 'map' | 'chart';
type LayoutMode = 'stacked' | 'side-by-side';

// Helper to convert lat/lon offset to x_ft/y_ft relative to source
function latLonToFeetOffset(
  sourceLat: number,
  sourceLon: number,
  wellLat: number,
  wellLon: number,
  flowDirection: number
): { x_ft: number; y_ft: number } {
  // Convert lat/lon difference to feet
  // 1 degree latitude = ~364,000 ft
  // 1 degree longitude = ~364,000 ft * cos(lat)
  const latDiffFt = (wellLat - sourceLat) * 364000;
  const lonDiffFt = (wellLon - sourceLon) * 364000 * Math.cos(sourceLat * Math.PI / 180);

  // Rotate to flow direction coordinates
  // x_ft is downgradient distance, y_ft is lateral offset
  const flowRad = (90 - flowDirection) * Math.PI / 180;

  const x_ft = lonDiffFt * Math.cos(flowRad) + latDiffFt * Math.sin(flowRad);
  const y_ft = -lonDiffFt * Math.sin(flowRad) + latDiffFt * Math.cos(flowRad);

  return { x_ft: Math.round(x_ft), y_ft: Math.round(y_ft) };
}

// Generate unique ID
function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ThreePlumeVisualization({
  centerLat,
  centerLon,
  threePlumeResult,
  flowDirection = 180,
  siteName,
  siteId,
  extractedGradientMap,
  extractedPlumeBoundaries,
  extractedWellInventory,
  depthToWaterFt = 25,
  aquiferThicknessFt = 30,
  onRerunWithWells,
  isSimulating = false,
  simulationLoading = false,
  bundledContaminantBoundaries,
  bundledGradientMap,
  bundledTimeline,
  bundledSnapshots,
  paneMode = 'all',
  controlledVisibleCompounds,
  onVisibleCompoundsChange,
  regLibrary,
}: ThreePlumeVisualizationProps) {
  const showBars = paneMode === 'all' || paneMode === 'bars-only';
  const show2D = paneMode === 'all' || paneMode === '2d-only';
  const show3D = paneMode === 'all' || paneMode === '3d-only';
  // Extract plume_states and contaminant_results (empty arrays when no result yet)
  const plume_states = threePlumeResult?.plume_states || [];
  const contaminant_results = threePlumeResult?.contaminant_results;

  // Stable object reference for source location — prevents useMemo invalidation downstream
  const sourceLatLon = useMemo(() => ({ lat: centerLat, lon: centerLon }), [centerLat, centerLon]);

  // selectedMonth is the time_months value (0, 6, 12, ...) NOT the array index
  const [selectedMonth, setSelectedMonth] = useState<number>(0);

  // v3.2 horizon currently driven by UnifiedTimeSlider — `0` means historical
  // / today (no overlay), 90 / 365 / 730 / 1825 / 3650 / 7300 are the v3.2
  // future nodes. Lives in the shared store so the bars-only-mode slider and
  // the 2d/3d-mode maps stay in sync.
  const selectedHorizonDays = usePolygonSliderStore(s => s.selectedHorizonDays);
  const setSelectedHorizonDays = usePolygonSliderStore(s => s.setSelectedHorizonDays);
  const [horizonPredictions, setHorizonPredictions] = useState<TimelinePredictionsResponse | null>(null);

  // Fetch v3.2 per-well predictions for this site once. The full timeline is
  // small (~6 horizons × ~N wells × ~M analytes) and fits in memory — slider
  // moves become instant pivots over an in-memory map.
  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    polygonApi.getTimelinePredictions(siteId)
      .then(r => { if (!cancelled) setHorizonPredictions(r); })
      .catch(() => { if (!cancelled) setHorizonPredictions(null); });
    return () => { cancelled = true; };
  }, [siteId]);

  /**
   * Per-(well_name, analyte_code_lower) → predicted_conc_mgl override at the
   * currently-selected horizon. Empty Map when `selectedHorizonDays === 0`
   * (the maps render measured data unchanged) OR when v3.2 hasn't run yet
   * for this site.
   *
   * Concentrations are kept in **mg/L** in this map (v3.2's native unit). The
   * 2D map consumes `concentration` in µg/L internally, so the bridge in
   * ThreePlumeMap multiplies overrides by 1000 — see comments there.
   */
  const wellHorizonOverrides = useMemo(() => {
    const m = new Map<string, Map<string, number>>();
    if (selectedHorizonDays === 0 || !horizonPredictions) return m;
    const slice = horizonPredictions.months?.find(s => s.horizon_days === selectedHorizonDays);
    if (!slice) return m;
    for (const p of slice.predictions || []) {
      if (!p.well_name || !p.analyte_code) continue;
      if (!Number.isFinite(p.predicted_conc_mgl) || p.predicted_conc_mgl < 0) continue;
      let byAnalyte = m.get(p.well_name);
      if (!byAnalyte) {
        byAnalyte = new Map();
        m.set(p.well_name, byAnalyte);
      }
      byAnalyte.set(p.analyte_code.toLowerCase(), p.predicted_conc_mgl);
    }
    return m;
  }, [horizonPredictions, selectedHorizonDays]);

  const [viewMode, setViewMode] = useState<ViewMode>('combined');
  const [show3DView, setShow3DView] = useState<boolean>(true);
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('stacked');

  // Layer visibility state (shared between 2D map and 3D view)
  const [visibleLayers, setVisibleLayers] = useState({
    psh: true,
    dissolved: true,
  });

  // Gradient map state
  const [gradientMap, setGradientMap] = useState<SiteGradientMap | null>(null);
  const [showGradientContours, setShowGradientContours] = useState<boolean>(true);

  // ============================================================================
  // UNIFIED TIME SLIDER state — lives in a zustand store so the slider (which
  // renders in bars-only mode under the MCL legend) and the maps (which
  // render in 2d-only / 3d-only modes lower on the page) share one source of
  // truth. Resetting on siteId change avoids carrying stale slider state
  // between sites.
  // ============================================================================
  const resetSliderForSite = usePolygonSliderStore(s => s.resetForSite);
  useEffect(() => { resetSliderForSite(siteId || null); }, [siteId, resetSliderForSite]);

  const timeSliderDate = usePolygonSliderStore(s => s.timeSliderDate);
  const setTimeSliderDate = usePolygonSliderStore(s => s.setTimeSliderDate);
  const [isHistoricalMode, setIsHistoricalMode] = useState<boolean>(false);
  const timeSliderBoundaries = usePolygonSliderStore(s => s.timeSliderBoundaries);
  const setTimeSliderBoundaries = usePolygonSliderStore(s => s.setTimeSliderBoundaries);
  const timeSliderFlowDirection = usePolygonSliderStore(s => s.timeSliderFlowDirection);
  const setTimeSliderFlowDirection = usePolygonSliderStore(s => s.setTimeSliderFlowDirection);

  // PSH well data from pre-computed snapshots (null = use API fallback)
  // When slider hits a snapshot, psh_wells are provided instantly (~30ms mesh gen).
  // When null, ThreePlume3DView falls back to usePSHData API call.
  const pshTargetDate = timeSliderDate || null;
  const snapshotPshWells = usePolygonSliderStore(s => s.snapshotPshWells);
  const setSnapshotPshWells = usePolygonSliderStore(s => s.setSnapshotPshWells);


  // All contaminant boundaries from well detections (unified dissolved plume)
  const [allContaminantBoundaries, setAllContaminantBoundaries] = useState<ContaminantBoundaryData[]>([]);

  // Sticky compound cache for the legend (declared early so the reg-library
  // refetch effect can clear it before the legend memoization runs).
  const seenPlumesRef = useRef<Map<string, CompoundPlumeData>>(new Map());

  // Extracted plume boundaries state (show by default at T=0)
  const [showExtractedBoundaries, setShowExtractedBoundaries] = useState<boolean>(true);

  // Per-compound visibility for dissolved plume (all detected compounds).
  // When the parent passes controlledVisibleCompounds, defer to that — lets the
  // page-level legend drive both the 2D and 3D map panes from a single source.
  const [internalVisibleCompounds, setInternalVisibleCompounds] = useState<Set<string>>(
    new Set()  // Start empty, auto-populate from API data
  );
  const visibleCompounds = controlledVisibleCompounds ?? internalVisibleCompounds;
  const setVisibleCompounds = (next: Set<string> | ((prev: Set<string>) => Set<string>)) => {
    const resolved = typeof next === 'function' ? next(visibleCompounds) : next;
    if (onVisibleCompoundsChange) onVisibleCompoundsChange(resolved);
    if (!controlledVisibleCompounds) setInternalVisibleCompounds(resolved);
  };
  // Legend dropdown — collapsed by default in bars-only mode (saves vertical
  // space at the top of the page); expanded by default in the inline modes.
  const [legendOpen, setLegendOpen] = useState<boolean>(paneMode !== 'bars-only');

  // Wells above concentration threshold (for exceedance markers) - aggregated from all compounds
  const [wellsAboveThreshold, setWellsAboveThreshold] = useState<Array<{
    well_id: string;
    lat: number;
    lon: number;
    concentration: number;
    compound?: string;
  }>>([]);

  // DW (Delineation Well) recommendations — non-delineated directions, updates with time slider
  const [dwRecommendations, setDwRecommendations] = useState<DWRecommendation[]>([]);

  // Remediation hotspots — wells with significantly elevated PSH or dissolved concentrations
  const [remediationHotspots, setRemediationHotspots] = useState<RemediationHotspot[]>([]);

  // Fetch ALL contaminant boundaries for unified dissolved plume (BTEX)
  // Uses the historical-state endpoint with today's date for time-consistent data
  // This ensures we only show data from the most recent samples, not all historical data
  // When bundledContaminantBoundaries is provided (from page-bundle), skip the API call entirely.
  useEffect(() => {
    if (!siteId) {
      return;
    }

    // Priority 0: Use bundled contaminant boundaries from page-bundle (zero network)
    // undefined = bundle not loaded yet (wait), null = loaded but empty, array = use it
    if (bundledContaminantBoundaries === undefined) {
      return; // Wait for bundle to load rather than making a 22s API call
    }

    if (bundledContaminantBoundaries && bundledContaminantBoundaries.length > 0) {
      const filtered = bundledContaminantBoundaries.filter(cb => isDisplayCompound(cb.compound));
      setAllContaminantBoundaries(filtered);

      // Aggregate wells above threshold from all compounds for exceedance markers
      const allWells: Array<{well_id: string; lat: number; lon: number; concentration: number; compound?: string}> = [];
      const seenWells = new Set<string>();
      for (const contaminant of filtered) {
        for (const well of contaminant.wells_with_detections || []) {
          if (!seenWells.has(well.well_id)) {
            seenWells.add(well.well_id);
            allWells.push({ well_id: well.well_id, lat: well.lat, lon: well.lon, concentration: well.concentration, compound: contaminant.compound });
          }
        }
      }
      setWellsAboveThreshold(allWells);

      const allCompoundNames = filtered.map(c => c.compound.toLowerCase());
      // Default to benzene only; user can toggle others on via the compound table
      const hasBenzene = allCompoundNames.includes('benzene');
      setVisibleCompounds(new Set(hasBenzene ? ['benzene'] : allCompoundNames));
      return;
    }

    const today = new Date().toISOString().split('T')[0];

    // Use historical-state endpoint with today's date for time-filtered data
    // This uses the same logic as the time slider - most recent sample per well ON OR BEFORE today
    // Avoids mixing data from different time periods which causes scattered "random blobs"
    import('../../api/client').then(({ apiClient }) => {
      apiClient.get<{
        compound_boundaries: Array<{
          compound: string;
          boundary: Array<[number, number]>;
          wells_with_detections: Array<{well_id: string; lat: number; lon: number; concentration: number; is_non_detect: boolean}>;
          wells_below_threshold: Array<{well_id: string; lat: number; lon: number; concentration: number; is_non_detect: boolean}>;
          max_concentration_ug_l: number;
          mcl_ug_l: number;
          threshold_ug_l: number;
          well_count: number;
        }>;
        flow_direction_deg: number;
      }>(`/polygon/physics/sites/${siteId}/historical-state/${today}`)
        .then(resp => {
          const data = resp.data;

          if (data.compound_boundaries && data.compound_boundaries.length > 0) {
            // Convert to ContaminantBoundaryData format
            const boundaries: ContaminantBoundaryData[] = data.compound_boundaries
              .filter(cb => isDisplayCompound(cb.compound))
              .map(cb => ({
                compound: cb.compound,
                boundary: cb.boundary,
                wells_with_detections: cb.wells_with_detections,
                wells_below_threshold: cb.wells_below_threshold,
                max_concentration_ug_l: cb.max_concentration_ug_l,
                mcl_ug_l: cb.mcl_ug_l,
                threshold_ug_l: cb.threshold_ug_l,
                well_count: cb.well_count,
              }));
            setAllContaminantBoundaries(boundaries);

            // Aggregate wells above threshold from all compounds for exceedance markers
            const allWells: Array<{well_id: string; lat: number; lon: number; concentration: number; compound?: string}> = [];
            const seenWells = new Set<string>();

            for (const contaminant of boundaries) {
              for (const well of contaminant.wells_with_detections || []) {
                // Only add each well once (use highest concentration if multiple compounds)
                if (!seenWells.has(well.well_id)) {
                  seenWells.add(well.well_id);
                  allWells.push({
                    well_id: well.well_id,
                    lat: well.lat,
                    lon: well.lon,
                    concentration: well.concentration,
                    compound: contaminant.compound,
                  });
                }
              }
            }
            setWellsAboveThreshold(allWells);

            // Auto-populate visible compounds with ALL compounds from API
            const allCompoundNames = boundaries.map(c => c.compound.toLowerCase());
            setVisibleCompounds(new Set(allCompoundNames));
          }
        })
        .catch(err => {
          console.error('[ThreePlumeVisualization] Failed to fetch historical-state boundaries:', err);
          // Fallback to physics-based endpoint (less time-consistent but still works)
          polygonApi.getAllContaminantPlumeBoundaries(siteId, 1.0, true)
            .then(data => {
              if (data.contaminants && data.contaminants.length > 0) {
                const filtered = data.contaminants.filter(c => isDisplayCompound(c.compound));
                setAllContaminantBoundaries(filtered);
                const allCompoundNames = filtered.map(c => c.compound.toLowerCase());
                setVisibleCompounds(new Set(allCompoundNames));
              }
            })
            .catch(fallbackErr => {
              console.error('[ThreePlumeVisualization] Fallback also failed:', fallbackErr);
            });
        });
    });
  }, [siteId, bundledContaminantBoundaries]);

  // Refetch boundaries when the user changes the regulatory library. We route
  // through getAllContaminantPlumeBoundaries (which supports library_slug)
  // rather than historical-state (which doesn't yet). Cascades into the plume
  // polygons + legend automatically because both read activeContaminantBoundaries.
  useEffect(() => {
    if (!siteId || !regLibrary) return;
    let cancelled = false;
    // Clear the sticky-cohort cache so stale MCL values from the previous
    // library don't linger in the legend.
    seenPlumesRef.current.clear();
    polygonApi.getAllContaminantPlumeBoundaries(siteId, 1.0, true, undefined, regLibrary)
      .then(data => {
        if (cancelled) return;
        if (data?.contaminants && data.contaminants.length > 0) {
          const filtered = data.contaminants.filter(c => isDisplayCompound(c.compound));
          setAllContaminantBoundaries(filtered);
        }
      })
      .catch(err => {
        console.warn('[ThreePlumeVisualization] reg-library refetch failed:', err);
      });
    return () => { cancelled = true; };
  }, [siteId, regLibrary]);

  // Fetch the IDW + marching-squares contour surface (same algorithm Phreatic's
  // Well Map uses) whenever we have a siteId. We intentionally IGNORE the
  // legacy `extractedGradientMap` and `bundledGradientMap` props — those carry
  // the old SOP T604 GradientCalculator output which produces visibly worse
  // contours from the same gauging data. The IDW result is canonical now.
  // The legacy props are kept as a fallback only when no siteId is present.
  useEffect(() => {
    if (siteId) {
      polygonApi.getWaterTableContours(siteId, { includeGrid: true })
        .then(data => {
          if (data) {
            setGradientMap(data);
          } else if (extractedGradientMap) {
            // IDW couldn't build a surface (fewer than 3 gauged wells, flat
            // surface, etc.) — fall through to whatever the bundle/nav-state
            // had so the user at least sees something.
            setGradientMap(extractedGradientMap);
          } else if (bundledGradientMap) {
            setGradientMap(bundledGradientMap);
          }
        })
        .catch(err => {
          console.warn('[POLYGON] IDW water-table fetch failed, falling back:', err);
          if (extractedGradientMap) setGradientMap(extractedGradientMap);
          else if (bundledGradientMap) setGradientMap(bundledGradientMap);
        });
      return;
    }

    // No siteId path — use legacy sources as-is.
    if (extractedGradientMap) {
      setGradientMap(extractedGradientMap);
    } else if (bundledGradientMap) {
      setGradientMap(bundledGradientMap);
    }
  }, [siteId, extractedGradientMap, bundledGradientMap]);

  // ============================================================================
  // PRE-COMPUTED BOUNDARIES: From three-plume model simulation (per time step)
  // ============================================================================

  // Pre-computed boundaries from three-plume model, keyed by time_months
  const precomputedBoundaryMap = useMemo(() => {
    if (!threePlumeResult?.precomputed_boundaries) return null;
    return threePlumeResult.precomputed_boundaries;
  }, [threePlumeResult]);

  // Boundaries for the current selectedMonth (from three-plume model)
  const threePlumeMonthBoundaries = useMemo((): ContaminantBoundaryData[] | null => {
    if (!precomputedBoundaryMap) return null;
    return precomputedBoundaryMap[selectedMonth] || [];
  }, [precomputedBoundaryMap, selectedMonth]);

  // ============================================================================
  // ACTIVE DATA: Time Slider (historical + predicted) OR Pre-computed OR Current
  // ============================================================================

  // Adapted contaminant boundaries with priority:
  // 1. UnifiedTimeSlider (historical/predicted) - user is scrubbing historical dates
  // 2. Pre-computed from three-plume model - shows evolving plume per selectedMonth
  // 3. Current well-measured boundaries (today's data) - default
  const activeContaminantBoundaries = useMemo((): ContaminantBoundaryData[] => {
    let boundaries: ContaminantBoundaryData[];
    if (timeSliderBoundaries !== null) {
      // Priority 1: UnifiedTimeSlider (historical/predicted) overrides everything
      boundaries = timeSliderBoundaries;
    } else if (threePlumeMonthBoundaries !== null && threePlumeMonthBoundaries.length > 0) {
      // Priority 2: Pre-computed boundaries from three-plume model for current month
      boundaries = threePlumeMonthBoundaries;
    } else {
      // Priority 3: Current well-measured boundaries (today's data)
      boundaries = allContaminantBoundaries;
    }

    // Filter to display compounds only (BTEX + TPH carbon ranges)
    return boundaries.filter(b => isDisplayCompound(b.compound));
  }, [timeSliderBoundaries, threePlumeMonthBoundaries, allContaminantBoundaries]);

  /**
   * MCL per compound under the user's currently-selected reg library. Source
   * of truth is `allContaminantBoundaries` (which the parent fetches with
   * the active `library_slug`) — that gives us the right MCL for every
   * compound the site is reporting on right now.
   *
   * Historical snapshots and the timeSlider's pre-computed predictive
   * snapshots carry FROZEN MCLs from whenever they were generated; without
   * this map the polygon shape would lag the dropdown. With it, the rehull
   * pass below always scores wells against the current reg library.
   */
  const currentMclByCompound = useMemo(() => {
    const m = new Map<string, number>();
    for (const b of allContaminantBoundaries || []) {
      if (b.compound && typeof b.mcl_ug_l === 'number') {
        m.set(b.compound.toLowerCase(), b.mcl_ug_l);
      }
    }
    return m;
  }, [allContaminantBoundaries]);

  /**
   * Boundaries handed to the 2D + 3D maps. Always passes through the
   * rehull helper so a reg-library change (today or historical scrub) or a
   * v3.2 horizon selection (future scrub) both re-partition wells and
   * redraw the polygon against the active MCL.
   */
  // NOTE: `predictionAdjustedBoundaries` is defined further down — once
  // `monitoringWells` (a useState below) has been declared. JavaScript's
  // temporal dead zone won't let us read `monitoringWells` here.

  // Time-varying water-table gradient generated from per-date DTW gauging
  // events. Refetched whenever the time slider date changes — the contours
  // evolve as the water table moves between gauging events. Falls back to the
  // static `gradientMap` when no live gradient is available for the date.
  const [timeVaryingGradient, setTimeVaryingGradient] = useState<SiteGradientMap | null>(null);
  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    polygonApi.getWaterTableAtDate(siteId, timeSliderDate || undefined)
      .then(data => {
        if (cancelled) return;
        // Only adopt the live gradient when it has actual contour data.
        if (data && data.contours && data.contours.length > 0) {
          setTimeVaryingGradient(data);
        } else {
          setTimeVaryingGradient(null);
        }
      })
      .catch(() => {
        if (!cancelled) setTimeVaryingGradient(null);
      });
    return () => { cancelled = true; };
  }, [siteId, timeSliderDate]);

  // Active gradient map: prefer the live, time-varying one when available.
  const activeGradientMap = timeVaryingGradient ?? gradientMap;

  // Enhanced plume_states - use well-based benzene boundary for dissolved plume when available
  const enhancedPlumeStates = useMemo(() => {
    // Find benzene boundary from active data source (historical or concentration-predicted)
    const benzeneBoundary = activeContaminantBoundaries.find(b =>
      b.compound.toLowerCase().includes('benzene') && !b.compound.toLowerCase().includes('ethyl')
    );

    if (!benzeneBoundary?.boundary || benzeneBoundary.boundary.length < 3) {
      return plume_states;
    }

    // Override the dissolved plume polygon with the benzene boundary (historical or predicted)
    return plume_states.map(state => ({
      ...state,
      dissolved: {
        ...state.dissolved,
        // Use benzene boundary as the dissolved plume polygon
        polygon: benzeneBoundary.boundary,
      },
    }));
  }, [plume_states, activeContaminantBoundaries]);

  // Generate multi-contaminant plume data for table and charts
  // PRIORITY: Use ALL contaminants from boundaries (measured well data) as the source of truth
  // Theoretical properties (colors, MCL, R-factor) are merged in for display purposes
  const multiContaminantPlumes = useMemo((): CompoundPlumeData[] => {
    const hasSimulationData = contaminant_results && contaminant_results.length > 0;
    const hasBoundaryData = activeContaminantBoundaries && activeContaminantBoundaries.length > 0;

    if (!hasSimulationData && !hasBoundaryData) {
      return [];
    }

    // Get base dissolved plume dimensions from current state
    const currentState = plume_states?.find(s => s.time_months === selectedMonth) || plume_states?.[0];
    const baseLengthFt = currentState?.dissolved?.length_ft || 500;
    const baseWidthFt = currentState?.dissolved?.width_ft || 200;
    const timeIndex = plume_states?.findIndex(s => s.time_months === selectedMonth) || 0;

    // Generate theoretical plumes for compound properties (colors, half-life, R-factor)
    const theoreticalPlumes = generateMultiContaminantPlumes(
      contaminant_results,
      baseLengthFt,
      baseWidthFt,
      threePlumeResult?.contaminant_type || 'Benzene',
      timeIndex
    );

    // MERGE theoretical + boundary: keep every compound from either source, but
    // when a compound exists in both, prefer the boundary entry (it carries the
    // measured well concentrations and polygon). Without this merge, the legend
    // collapses to only boundary-detected compounds once boundary data loads.
    if (hasBoundaryData) {
      const plumesFromBoundaries: CompoundPlumeData[] = activeContaminantBoundaries.map((boundary: ContaminantBoundaryData) => {
        const theoreticalMatch = theoreticalPlumes.find((p: CompoundPlumeData) => {
          const plumeCompound = p.compound.toLowerCase();
          const boundaryCompound = boundary.compound.toLowerCase();
          return boundaryCompound.includes(plumeCompound) || plumeCompound.includes(boundaryCompound);
        });
        const props = getCompoundProperties(boundary.compound);
        return {
          compound: boundary.compound,
          color: theoreticalMatch?.color || props.plume_color || '#9ca3af',
          lengthFt: theoreticalMatch?.lengthFt || baseLengthFt,
          widthFt: theoreticalMatch?.widthFt || baseWidthFt,
          concentration_ug_l: boundary.max_concentration_ug_l,
          mcl_ug_l: boundary.mcl_ug_l || boundary.threshold_ug_l,
          exceedsMcl: boundary.max_concentration_ug_l > (boundary.mcl_ug_l || boundary.threshold_ug_l || 0),
          relativeLength: theoreticalMatch?.relativeLength || 1.0,
          retardationFactor: theoreticalMatch?.retardationFactor || props.retardation_factor || 1.0,
          halfLifeDays: theoreticalMatch?.halfLifeDays || props.half_life_days || 365,
          polygon: boundary.boundary && boundary.boundary.length >= 3 ? boundary.boundary : undefined,
          sourceType: 'measured' as const,
          wells: boundary.wells_with_detections,
          wells_below_threshold: boundary.wells_below_threshold,
        };
      });

      // Append theoretical plumes whose compound isn't already in the boundary
      // set. Use EXACT lowercase match — substring matching wrongly drops e.g.
      // "Ethylbenzene" when boundary has "Benzene" because the latter is a
      // substring of the former.
      const boundaryCompoundsLower = new Set(plumesFromBoundaries.map(p => p.compound.toLowerCase().trim()));
      const remainingTheoretical = theoreticalPlumes.filter((p: CompoundPlumeData) => {
        const plumeCompoundLower = p.compound.toLowerCase().trim();
        return !boundaryCompoundsLower.has(plumeCompoundLower);
      });

      const merged = [...plumesFromBoundaries, ...remainingTheoretical];
      // Sort by concentration (highest first) for proper layering
      merged.sort((a, b) => b.concentration_ug_l - a.concentration_ug_l);
      return merged;
    }

    // Fallback: use theoretical plumes if no boundary data
    return theoreticalPlumes;
  }, [contaminant_results, plume_states, selectedMonth, threePlumeResult?.contaminant_type, activeContaminantBoundaries]);

  // Sticky compound set: once a compound has appeared in the live computation,
  // keep it in the legend until the user changes regulatory library (which
  // clears the cache via the reg-library refetch effect, so stale MCL values
  // from the previous library don't linger on rows that disappear from the
  // refreshed boundary fetch).
  const stickyPlumes = useMemo(() => {
    // Update the seen-set with the freshest data per compound
    for (const p of multiContaminantPlumes) {
      seenPlumesRef.current.set(p.compound.toLowerCase().trim(), p);
    }
    // Result = current live data + any historically-seen compound missing now
    const currentLower = new Set(multiContaminantPlumes.map(p => p.compound.toLowerCase().trim()));
    const result = [...multiContaminantPlumes];
    for (const [key, plume] of seenPlumesRef.current.entries()) {
      if (!currentLower.has(key)) result.push(plume);
    }
    result.sort((a, b) => b.concentration_ug_l - a.concentration_ug_l);
    return result;
  }, [multiContaminantPlumes]);

  // Initialize visibleCompounds ONCE when plume data first arrives — show ALL
  // detected compounds by default. The ref prevents this from re-firing every
  // time the user clears the legend; otherwise the user can never have zero
  // compounds selected (toggling off the last one would re-fill the set).
  const compoundsInitializedRef = useRef(false);
  useEffect(() => {
    if (compoundsInitializedRef.current) return;
    if (multiContaminantPlumes.length > 0) {
      const allCompounds = multiContaminantPlumes.map((p: CompoundPlumeData) => p.compound.toLowerCase());
      setVisibleCompounds(new Set(allCompounds));
      compoundsInitializedRef.current = true;
    }
  }, [multiContaminantPlumes]);

  // Auto-merge benzene from time slider into visibleCompounds if not already present.
  // Other compounds stay hidden unless the user toggles them on.
  useEffect(() => {
    if (activeContaminantBoundaries.length > 0 && visibleCompounds.size > 0) {
      const hasBenzene = activeContaminantBoundaries.some(b => b.compound.toLowerCase() === 'benzene');
      if (hasBenzene && !visibleCompounds.has('benzene')) {
        setVisibleCompounds(prev => {
          const updated = new Set(prev);
          updated.add('benzene');
          return updated;
        });
      }
    }
  }, [activeContaminantBoundaries]);

  // Fetch sentinel alerts (WI recommendations + remediation hotspots) — only for current/future dates.
  // Historical scrubbing skips this entirely (no sentinel value for past dates).
  useEffect(() => {
    if (!siteId || !timeSliderDate || isHistoricalMode) {
      setDwRecommendations([]);
      setRemediationHotspots([]);
      return;
    }
    polygonApi.getSentinelAlerts(siteId, timeSliderDate)
      .then(data => {
        const recs = (data.recommendations || []).filter(r => isRealWellName(r.trigger_well_name));
        setDwRecommendations(recs);
        setRemediationHotspots(data.remediation_hotspots || []);
      })
      .catch(() => {
        setDwRecommendations([]);
        setRemediationHotspots([]);
      });
  }, [siteId, timeSliderDate, isHistoricalMode]);

  // Handler for toggling individual compound visibility
  // All compound names stored in lowercase for consistency
  const handleCompoundToggle = useCallback((compound: string, isVisible: boolean) => {
    const lowerCompound = compound.toLowerCase();
    setVisibleCompounds((prev: Set<string>) => {
      const newSet = new Set(prev);
      if (isVisible) {
        newSet.add(lowerCompound);
      } else {
        newSet.delete(lowerCompound);
      }
      return newSet;
    });
  }, []);

  // Handler for toggling all compounds — uses stickyPlumes so "Show All" turns
  // on every compound that's ever appeared, not just the currently-live subset.
  const handleToggleAllCompounds = useCallback((showAll: boolean) => {
    if (showAll) {
      setVisibleCompounds(new Set(stickyPlumes.map((p: CompoundPlumeData) => p.compound.toLowerCase())));
    } else {
      setVisibleCompounds(new Set());
    }
  }, [stickyPlumes]);

  // Well state management
  const [recoveryWells, setRecoveryWells] = useState<RecoveryWell[]>([]);
  const [mdpeWells, setMdpeWells] = useState<MDPEWell[]>([]);
  const [monitoringWells, setMonitoringWells] = useState<Array<{
    id: string;
    well_id: string;
    latitude: number;
    longitude: number;
    depth_ft?: number;
  }>>([]);
  const [wellPlacementMode, setWellPlacementMode] = useState<WellPlacementMode>(null);
  const [pendingWellLocation, setPendingWellLocation] = useState<{ lat: number; lon: number } | null>(null);
  const [showWellModal, setShowWellModal] = useState(false);
  const [hasUnsavedWells, setHasUnsavedWells] = useState(false);

  // Well_name → lat/lon map so the polygon rehull can seed wells that v3.2
  // newly predicts to exceed but were never sampled (so they're absent from
  // boundary.wells_with_detections / wells_below_threshold). Declared here,
  // after monitoringWells, to dodge the temporal-dead-zone error const
  // declarations create when forward-referenced.
  const wellLocationByName = useMemo(() => {
    const m = new Map<string, { lat: number; lon: number }>();
    for (const w of monitoringWells || []) {
      if (w.well_id && typeof w.latitude === 'number' && typeof w.longitude === 'number') {
        m.set(w.well_id, { lat: w.latitude, lon: w.longitude });
      }
    }
    return m;
  }, [monitoringWells]);

  /**
   * Boundaries handed to the 2D + 3D maps. Always passes through the rehull
   * helper so a reg-library change (today or historical scrub) or a v3.2
   * horizon selection (future scrub) both re-partition wells and redraw the
   * polygon against the active MCL. `wellLocationByName` lets the helper
   * include wells that v3.2 newly predicts to exceed but were never sampled.
   */
  const predictionAdjustedBoundaries = useMemo(() => {
    return applyHorizonOverridesToBoundaries(
      activeContaminantBoundaries,
      wellHorizonOverrides,
      selectedHorizonDays,
      currentMclByCompound,
      wellLocationByName,
    );
  }, [activeContaminantBoundaries, wellHorizonOverrides, selectedHorizonDays, currentMclByCompound, wellLocationByName]);

  // Auto-populate wells from extracted well inventory
  useEffect(() => {
    if (!extractedWellInventory || extractedWellInventory.length === 0) {
      return;
    }


    // Filter and convert recovery wells
    const extractedRecoveryWells = extractedWellInventory
      .filter(w => w.well_type === 'recovery' && w.lat && w.lon)
      .map(w => {
        const { x_ft, y_ft } = latLonToFeetOffset(centerLat, centerLon, w.lat, w.lon, flowDirection);
        // Convert gpm to gpd (gallons per minute to gallons per day)
        const pumpRateGpd = (w.pump_rate_gpm || 5) * 60 * 24; // Default 5 gpm if not specified
        return {
          id: generateId(),
          x_ft,
          y_ft,
          pump_rate_gpd: pumpRateGpd,
          start_month: 0, // Start immediately
          latitude: w.lat,
          longitude: w.lon,
          depth_ft: w.depth_ft,
        } as RecoveryWell;
      });

    // Filter and convert MDPE wells
    const extractedMdpeWells = extractedWellInventory
      .filter(w => w.well_type === 'mdpe' && w.lat && w.lon)
      .map(w => {
        const { x_ft, y_ft } = latLonToFeetOffset(centerLat, centerLon, w.lat, w.lon, flowDirection);
        // Default extraction rate - convert gpm to cfm (approximate vapor equivalent)
        const extractionCfm = (w.pump_rate_gpm || 10) * 7.5; // Rough conversion
        return {
          id: generateId(),
          x_ft,
          y_ft,
          extraction_cfm: extractionCfm,
          start_month: 0, // Start immediately
          latitude: w.lat,
          longitude: w.lon,
          depth_ft: w.depth_ft,
        } as MDPEWell;
      });

    // Filter and convert monitoring wells (for display only)
    const extractedMonitoringWells = extractedWellInventory
      .filter(w => (w.well_type === 'monitoring' || w.well_type === 'monitor') && w.lat && w.lon)
      .map(w => ({
        id: generateId(),
        well_id: w.well_id || 'MW',
        latitude: w.lat,
        longitude: w.lon,
        depth_ft: w.depth_ft,
      }));

    if (extractedRecoveryWells.length > 0) {
      setRecoveryWells(extractedRecoveryWells);
      setHasUnsavedWells(true);
    } else {
    }

    if (extractedMdpeWells.length > 0) {
      setMdpeWells(extractedMdpeWells);
      setHasUnsavedWells(true);
    }

    if (extractedMonitoringWells.length > 0) {
      setMonitoringWells(extractedMonitoringWells);
    }
  }, [extractedWellInventory, centerLat, centerLon, flowDirection]);

  // Handle month change from ThreePlumeMap (debounced via requestAnimationFrame)
  const pendingMonth = useRef<number>(0);
  const sliderRaf = useRef<number>(0);
  const handleMonthChange = useCallback((timeMonths: number) => {
    pendingMonth.current = timeMonths;
    if (!sliderRaf.current) {
      sliderRaf.current = requestAnimationFrame(() => {
        setSelectedMonth(pendingMonth.current);
        sliderRaf.current = 0;
      });
    }
  }, []);

  // Stable callbacks for UnifiedTimeSlider (avoid new refs every render)
  const handleTimeSliderBoundaries = useCallback((boundaries: ContaminantBoundaryData[] | null) => {
    setTimeSliderBoundaries(boundaries);
  }, []);

  const handleTimeSliderFlowDirection = useCallback((direction: number | null) => {
    setTimeSliderFlowDirection(direction);
  }, []);

  const handleTimeSliderPshWells = useCallback((wells: typeof snapshotPshWells) => {
    setSnapshotPshWells(wells);
  }, []);

  const handleTimeSliderDateChange = useCallback((date: string, isHistorical: boolean) => {
    setTimeSliderDate(date);
    setIsHistoricalMode(isHistorical);
    if (!isHistorical && date) {
      const now = new Date();
      const target = new Date(date);
      const monthOffset = Math.max(0, Math.round(
        (target.getTime() - now.getTime()) / (30.44 * 24 * 60 * 60 * 1000)
      ));
      handleMonthChange(monthOffset);
    } else {
      handleMonthChange(0);
    }
  }, [handleMonthChange]);

  // O(1) month lookup — pre-build Map from plume_states
  const stateByMonth = useMemo(() => {
    const states = enhancedPlumeStates || plume_states;
    const map = new Map<number, ThreePlumeState>();
    for (const s of states) map.set(s.time_months, s);
    return map;
  }, [enhancedPlumeStates, plume_states]);

  // Get the current plume state by time_months value — O(1) Map lookup
  const currentPlumeStateFor3D = useMemo(() => {
    return stateByMonth.get(selectedMonth) || (enhancedPlumeStates || plume_states)?.[0];
  }, [stateByMonth, selectedMonth, enhancedPlumeStates, plume_states]);

  /**
   * When the v3.2 horizon slider is past "Now", replace the 3D view's
   * synthetic `concentration_wells` (used for IDW gradient rendering) with
   * the v3.2-predicted values at the chosen horizon. The 3D gradient then
   * shifts to show the forecast field. Falls through unchanged when no
   * overrides exist or "Now" is selected.
   */
  const horizonAdjustedPlumeStateFor3D = useMemo(() => {
    if (!currentPlumeStateFor3D) return null;
    if (selectedHorizonDays === 0 || wellHorizonOverrides.size === 0) return currentPlumeStateFor3D;

    // Index monitoring wells by display name so we can find each override's lat/lon.
    const wellLatLon = new Map<string, { lat: number; lon: number }>();
    for (const w of monitoringWells || []) {
      if (w.well_id && typeof w.latitude === 'number' && typeof w.longitude === 'number') {
        wellLatLon.set(w.well_id, { lat: w.latitude, lon: w.longitude });
      }
    }

    // Build a synthetic concentration_wells[] using the MAX predicted_conc_mgl
    // per well across visible analytes (max so the gradient reflects the
    // worst-case contaminant at that location). Values converted mg/L → µg/L
    // to match the existing 3D field's units.
    const visibleSet = visibleCompounds && visibleCompounds.size > 0 ? visibleCompounds : null;
    const synthetic: Array<{ lat: number; lon: number; concentration: number }> = [];
    for (const [wellName, byAnalyte] of wellHorizonOverrides.entries()) {
      const loc = wellLatLon.get(wellName);
      if (!loc) continue;
      let peak = 0;
      for (const [code, mgl] of byAnalyte.entries()) {
        if (visibleSet && !visibleSet.has(code) && !visibleSet.has(code.toLowerCase())) continue;
        if (Number.isFinite(mgl) && mgl > peak) peak = mgl;
      }
      if (peak > 0) {
        synthetic.push({ lat: loc.lat, lon: loc.lon, concentration: peak * 1000 });
      }
    }

    if (synthetic.length === 0) return currentPlumeStateFor3D;
    return {
      ...currentPlumeStateFor3D,
      dissolved: {
        ...currentPlumeStateFor3D.dissolved,
        concentration_wells: synthetic,
        max_conc_ug_l: Math.max(
          currentPlumeStateFor3D.dissolved?.max_conc_ug_l ?? 0,
          ...synthetic.map(s => s.concentration),
        ),
      },
    };
  }, [currentPlumeStateFor3D, selectedHorizonDays, wellHorizonOverrides, monitoringWells, visibleCompounds]);

  // Handle layer visibility changes (shared between 2D and 3D views)
  const handleVisibleLayersChange = useCallback((layers: typeof visibleLayers) => {
    setVisibleLayers(layers);
  }, []);

  // Handle well placement from map click
  const handleWellPlaced = useCallback((type: 'recovery' | 'mdpe', lat: number, lon: number) => {
    setPendingWellLocation({ lat, lon });
    setShowWellModal(true);
  }, []);

  // Handle well parameters saved from modal
  const handleWellSaved = useCallback((params: WellParams) => {
    if (!pendingWellLocation || !wellPlacementMode) return;

    const { x_ft, y_ft } = latLonToFeetOffset(
      centerLat,
      centerLon,
      pendingWellLocation.lat,
      pendingWellLocation.lon,
      flowDirection
    );

    if (wellPlacementMode === 'recovery') {
      const newWell: RecoveryWell = {
        id: generateId(),
        x_ft,
        y_ft,
        pump_rate_gpd: params.pumpRateGpd || 5000,
        start_month: params.startMonth,
        latitude: pendingWellLocation.lat,
        longitude: pendingWellLocation.lon,
      };
      setRecoveryWells(prev => [...prev, newWell]);
    } else {
      const newWell: MDPEWell = {
        id: generateId(),
        x_ft,
        y_ft,
        extraction_cfm: params.extractionCfm || 100,
        start_month: params.startMonth,
        latitude: pendingWellLocation.lat,
        longitude: pendingWellLocation.lon,
      };
      setMdpeWells(prev => [...prev, newWell]);
    }

    setHasUnsavedWells(true);
    setPendingWellLocation(null);
    setWellPlacementMode(null);
  }, [pendingWellLocation, wellPlacementMode, centerLat, centerLon, flowDirection]);

  // Handle well removal
  const handleWellRemoved = useCallback((type: 'recovery' | 'mdpe', id: string) => {
    if (type === 'recovery') {
      setRecoveryWells(prev => prev.filter(w => w.id !== id));
    } else {
      setMdpeWells(prev => prev.filter(w => w.id !== id));
    }
    setHasUnsavedWells(true);
  }, []);

  // Handle well toggle (enable/disable)
  const handleRecoveryWellToggle = useCallback((wellId: string, enabled: boolean) => {
    setRecoveryWells(prev => prev.map(w =>
      w.id === wellId ? { ...w, enabled } : w
    ));
    setHasUnsavedWells(true);
  }, []);

  const handleMdpeWellToggle = useCallback((wellId: string, enabled: boolean) => {
    setMdpeWells(prev => prev.map(w =>
      w.id === wellId ? { ...w, enabled } : w
    ));
    setHasUnsavedWells(true);
  }, []);

  // Handle well deletion from table
  const handleRecoveryWellDelete = useCallback((wellId: string) => {
    setRecoveryWells(prev => prev.filter(w => w.id !== wellId));
    setHasUnsavedWells(true);
  }, []);

  const handleMdpeWellDelete = useCallback((wellId: string) => {
    setMdpeWells(prev => prev.filter(w => w.id !== wellId));
    setHasUnsavedWells(true);
  }, []);

  // Handle re-run simulation with wells
  const handleRerunSimulation = useCallback(() => {
    if (onRerunWithWells) {
      onRerunWithWells(recoveryWells, mdpeWells);
      setHasUnsavedWells(false);
    }
  }, [onRerunWithWells, recoveryWells, mdpeWells]);

  // Clear all wells
  const handleClearWells = useCallback(() => {
    setRecoveryWells([]);
    setMdpeWells([]);
    setHasUnsavedWells(true);
  }, []);

  const hasSimulationResult = plume_states && plume_states.length > 0;

  return (
    <div className="space-y-4">
      {/* Outside-the-container title + Recovery/MDPE placement buttons removed
          per UX request — all titles live inside the map container, and
          remediation-well placement is handled elsewhere (Pump Control / Well
          Intelligence panels). */}

      {/* Well Summary & Re-run Button — map-related, hide in bars-only */}
      {(show2D || show3D) && onRerunWithWells && (recoveryWells.length > 0 || mdpeWells.length > 0) && (
        <div className="flex items-center justify-between bg-mono-900 border border-mono-800 rounded-lg p-3">
          <div className="flex items-center gap-4 text-xs">
            {recoveryWells.length > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-mono-400" />
                <span className="text-mono-300 font-medium">{recoveryWells.length} Recovery Well{recoveryWells.length > 1 ? 's' : ''}</span>
                <span className="text-mono-500">
                  ({recoveryWells.reduce((sum, w) => sum + w.pump_rate_gpd, 0).toLocaleString()} GPD total)
                </span>
              </div>
            )}
            {mdpeWells.length > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-mono-500" />
                <span className="text-mono-300 font-medium">{mdpeWells.length} MDPE Well{mdpeWells.length > 1 ? 's' : ''}</span>
                <span className="text-mono-500">
                  ({mdpeWells.reduce((sum, w) => sum + w.extraction_cfm, 0).toLocaleString()} CFM total)
                </span>
              </div>
            )}
          </div>
          <button
            onClick={handleRerunSimulation}
            disabled={isSimulating || !hasUnsavedWells}
            className={`px-4 py-2 text-xs font-medium rounded flex items-center gap-2 transition-all ${
              hasUnsavedWells && !isSimulating
                ? 'bg-mono-100 hover:bg-white text-mono-900'
                : 'bg-mono-800 text-mono-500 cursor-not-allowed'
            }`}
          >
            <ArrowPathIcon className={`w-4 h-4 ${isSimulating ? 'animate-spin' : ''}`} />
            {isSimulating ? 'Simulating...' : 'Re-run with Wells'}
          </button>
        </div>
      )}

      {/* Contaminant stats cards moved to <ContaminantStatsColumn /> in
          PolygonSiteModeling so they can live as a vertical column alongside
          Cost + Reg Library. The bars-only paneMode now only renders the
          Contaminants vs MCL legend below. */}

      {/* Simulation loading indicator — small fixed pill in the top-right
          corner so it doesn't push other content. Only render in bars-only
          mode (top of page) to avoid duplication across pane instances. */}
      {paneMode === 'bars-only' && simulationLoading && !hasSimulationResult && (
        <div className="fixed top-3 right-3 z-50 bg-mono-900/95 border border-cyan-500/40 rounded-full px-3 py-1 flex items-center gap-2 shadow-lg backdrop-blur-sm">
          <ArrowPathIcon className="w-3 h-3 text-cyan-400 animate-spin flex-shrink-0" />
          <span className="text-[10px] text-mono-300 tracking-wide">simulating plume…</span>
        </div>
      )}

      {/* Visualization Content */}
      {/* 2D Map + 3D View Layout */}
      <div className={layoutMode === 'side-by-side' ? 'grid grid-cols-2 gap-4' : 'space-y-4'}>
        {/* 2D Map */}
        {show2D && (
        <div className={layoutMode === 'side-by-side' ? 'min-h-[500px]' : ''}>
          <ThreePlumeMap
            centerLat={centerLat}
            centerLon={centerLon}
            plumeStates={enhancedPlumeStates}
            flowDirection={flowDirection}
            initialMonth={selectedMonth}
            onMonthChange={handleMonthChange}
            showControls={true}
            visibleLayers={visibleLayers}
            onVisibleLayersChange={handleVisibleLayersChange}
            recoveryWells={recoveryWells}
            mdpeWells={mdpeWells}
            monitoringWells={monitoringWells}
            wellPlacementMode={wellPlacementMode}
            onWellPlaced={handleWellPlaced}
            onWellRemoved={handleWellRemoved}
            gradientMap={activeGradientMap}
            showGradientContours={showGradientContours}
            onGradientContoursToggle={setShowGradientContours}
            extractedPlumeBoundaries={extractedPlumeBoundaries}
            showExtractedBoundaries={showExtractedBoundaries}
            onExtractedBoundariesToggle={setShowExtractedBoundaries}
            contaminantResults={contaminant_results}
            showMultiContaminant={true}
            visibleCompounds={visibleCompounds}
            wellsAboveThreshold={wellsAboveThreshold}
            wellBoundaries={predictionAdjustedBoundaries}
            wellHorizonOverrides={wellHorizonOverrides}
            horizonDays={selectedHorizonDays}
            siteId={siteId}
            pshTargetDate={timeSliderDate || null}
            dwRecommendations={dwRecommendations}
            remediationHotspots={remediationHotspots}
          />
        </div>
        )}

        {/* 3D Cross-Section View */}
        {show3D && horizonAdjustedPlumeStateFor3D && (
          <div className={layoutMode === 'side-by-side' ? 'min-h-[500px]' : ''}>
            <ThreePlume3DView
              plumeState={horizonAdjustedPlumeStateFor3D}
              depthToWaterFt={depthToWaterFt}
              aquiferThicknessFt={aquiferThicknessFt}
              flowDirection={flowDirection}
              recoveryWells={recoveryWells}
              mdpeWells={mdpeWells}
              monitoringWells={monitoringWells}
              sourceLatLon={sourceLatLon}
              visibleLayers={visibleLayers}
              initialPshMassLbs={threePlumeResult?.initial_psh_mass_lbs ?? 0}
              peakDissolvedConcUgL={threePlumeResult?.peak_dissolved_conc_ug_l ?? 0}
              gradientMap={activeGradientMap}
              showGradientContours={showGradientContours}
              multiContaminantBoundaries={predictionAdjustedBoundaries}
              visibleCompounds={visibleCompounds}
              wellHorizonOverrides={wellHorizonOverrides}
              horizonDays={selectedHorizonDays}
              siteId={siteId}
              pshTargetDate={timeSliderDate || null}
              pshWellsFromSnapshot={snapshotPshWells}
            />
          </div>
        )}
      </div>

      {/* Contaminants vs MCL — collapsible dropdown legend.
          Renders from stickyPlumes so once a compound has appeared, it stays
          listed even if a later data refresh returns a smaller subset. */}
      {showBars && stickyPlumes.length > 0 && (
        <div className="bg-mono-900 border border-mono-800 rounded-lg">
          <div className="flex items-center justify-between p-3">
            <button
              onClick={() => setLegendOpen(o => !o)}
              className="flex items-center gap-2 text-left flex-1"
            >
              <span className={`text-[10px] text-mono-400 transition-transform ${legendOpen ? 'rotate-90' : ''}`}>&#9654;</span>
              <h3 className="text-xs font-semibold text-mono-300 uppercase tracking-wider">
                Contaminants vs MCL
              </h3>
              <span className="text-[10px] text-mono-500 ml-1">
                ({Array.from(visibleCompounds).filter(c => stickyPlumes.some(p => p.compound.toLowerCase() === c)).length}/{stickyPlumes.length} visible)
              </span>
            </button>
            {legendOpen && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleToggleAllCompounds(true)}
                  className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                    !visibleCompounds || stickyPlumes.every(p => visibleCompounds.has(p.compound.toLowerCase()))
                      ? 'bg-mono-700 text-mono-200'
                      : 'bg-mono-800 text-mono-400 hover:bg-mono-700'
                  }`}
                >
                  Show All
                </button>
                <button
                  onClick={() => handleToggleAllCompounds(false)}
                  className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                    visibleCompounds && stickyPlumes.every(p => !visibleCompounds.has(p.compound.toLowerCase()))
                      ? 'bg-mono-700 text-mono-200'
                      : 'bg-mono-800 text-mono-400 hover:bg-mono-700'
                  }`}
                >
                  Hide All
                </button>
              </div>
            )}
          </div>
          {legendOpen && (
          <div className="px-4 pb-4">
          {/* Table header */}
          <div className="flex items-center gap-3 mb-2 px-0.5">
            <div className="w-3.5 flex-shrink-0" />
            <div className="w-28 flex-shrink-0 text-[9px] text-mono-600 uppercase tracking-wider">Compound</div>
            <div className="w-20 flex-shrink-0 text-[9px] text-mono-600 uppercase tracking-wider text-right">Conc.</div>
            <div className="w-20 flex-shrink-0 text-[9px] text-mono-600 uppercase tracking-wider text-right">MCL</div>
            <div className="flex-1 text-[9px] text-mono-600 uppercase tracking-wider text-center">Exceedance</div>
            <div className="w-16 flex-shrink-0 text-[9px] text-mono-600 uppercase tracking-wider text-right">Ratio</div>
          </div>
          <div className="space-y-1.5">
            {stickyPlumes.map((plume) => {
              const isVisible = !visibleCompounds || visibleCompounds.has(plume.compound.toLowerCase());
              const mclRatio = plume.mcl_ug_l ? plume.concentration_ug_l / plume.mcl_ug_l : 0;
              const exceeds = mclRatio > 1;
              const concentration_mg_l = plume.concentration_ug_l / 1000;
              const mcl_mg_l = plume.mcl_ug_l ? plume.mcl_ug_l / 1000 : null;

              // Log-scale bar: maps 0.1x → 0%, 1x → 25%, 10x → 50%, 100x → 75%, 1000x → 100%
              const logRatio = mclRatio > 0 ? Math.log10(mclRatio) : -2;
              const barPct = Math.max(0, Math.min(100, (logRatio + 1) * 25));
              // MCL threshold line always at 25% (where logRatio=0, i.e., 1x MCL)
              const mclLinePct = 25;

              return (
                <div
                  key={plume.compound}
                  className={`flex items-center gap-3 py-1 rounded px-0.5 hover:bg-mono-800/50 transition-colors ${!isVisible ? 'opacity-40' : ''}`}
                >
                  {/* Toggle checkbox */}
                  <input
                    type="checkbox"
                    checked={isVisible}
                    onChange={(e) => handleCompoundToggle(plume.compound, e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-mono-600 bg-mono-800 text-mono-400 focus:ring-0 cursor-pointer flex-shrink-0"
                  />
                  {/* Color swatch + name */}
                  <div className="flex items-center gap-1.5 w-28 flex-shrink-0">
                    <div
                      className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                      style={{ backgroundColor: plume.color }}
                    />
                    <span className="text-xs text-mono-200 font-medium truncate">{plume.compound}</span>
                  </div>
                  {/* Concentration */}
                  <div className="w-20 flex-shrink-0 text-right">
                    <span className="text-[11px] text-mono-300 tabular-nums font-mono">
                      {concentration_mg_l < 0.001 ? concentration_mg_l.toExponential(1) : concentration_mg_l < 0.01 ? concentration_mg_l.toFixed(4) : concentration_mg_l.toFixed(3)}
                    </span>
                  </div>
                  {/* MCL */}
                  <div className="w-20 flex-shrink-0 text-right">
                    <span className="text-[11px] text-mono-500 tabular-nums font-mono">
                      {mcl_mg_l != null ? (mcl_mg_l < 0.001 ? mcl_mg_l.toExponential(1) : mcl_mg_l.toFixed(3)) : '—'}
                    </span>
                  </div>
                  {/* Log-scale exceedance bar */}
                  <div className="flex-1 h-4 bg-mono-800 rounded overflow-hidden relative">
                    {/* Scale tick marks */}
                    {[0, 25, 50, 75].map(pct => (
                      <div
                        key={pct}
                        className="absolute top-0 bottom-0 w-px bg-mono-700/50"
                        style={{ left: `${pct}%` }}
                      />
                    ))}
                    {/* MCL threshold line */}
                    {mcl_mg_l != null && (
                      <div
                        className="absolute top-0 bottom-0 w-0.5 bg-red-500/80 z-10"
                        style={{ left: `${mclLinePct}%` }}
                      />
                    )}
                    {/* Concentration bar */}
                    <div
                      className="h-full rounded transition-all duration-300"
                      style={{
                        width: `${barPct}%`,
                        backgroundColor: exceeds ? '#ef4444' : '#22c55e',
                        opacity: isVisible ? 0.7 : 0.3,
                      }}
                    />
                    {/* Scale labels inside bar area */}
                    <div className="absolute inset-0 flex items-center pointer-events-none">
                      <span className="absolute text-[7px] text-mono-600" style={{ left: '25%', transform: 'translateX(-50%)' }}>1x</span>
                      <span className="absolute text-[7px] text-mono-600" style={{ left: '50%', transform: 'translateX(-50%)' }}>10x</span>
                      <span className="absolute text-[7px] text-mono-600" style={{ left: '75%', transform: 'translateX(-50%)' }}>100x</span>
                    </div>
                  </div>
                  {/* Exceedance ratio badge */}
                  <div className="w-16 flex-shrink-0 text-right">
                    {mcl_mg_l != null ? (
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold tabular-nums ${
                        exceeds
                          ? 'bg-red-500/15 text-red-400 border border-red-500/30'
                          : 'bg-green-500/15 text-green-400 border border-green-500/30'
                      }`}>
                        {mclRatio >= 100 ? `${Math.round(mclRatio)}x` : mclRatio >= 10 ? `${mclRatio.toFixed(0)}x` : `${mclRatio.toFixed(1)}x`}
                      </span>
                    ) : (
                      <span className="text-[10px] text-mono-600">—</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-[9px] text-mono-600 mt-2 flex items-center gap-4 border-t border-mono-800 pt-2">
            <div className="flex items-center gap-1">
              <div className="w-0.5 h-2.5 bg-red-500/80 rounded" />
              <span>MCL threshold</span>
            </div>
            <span>Concentrations in mg/L</span>
            <span className="text-mono-700">Log scale: 0.1x | 1x | 10x | 100x | 1000x</span>
          </div>
          </div>
          )}
        </div>
      )}

      {/* Unified Timeline Slider — sits under the MCL legend in bars-only
          mode (the top-of-page instance). The slider's emitted state lives
          in usePolygonSliderStore so the 2D and 3D maps lower on the page
          read the same selectedHorizonDays / timeSliderBoundaries / PSH
          wells without any prop-drilling. */}
      {showBars && siteId && (
        <UnifiedTimeSlider
          siteId={siteId}
          currentBoundaries={allContaminantBoundaries}
          currentFlowDirection={flowDirection}
          onBoundariesChange={handleTimeSliderBoundaries}
          onFlowDirectionChange={handleTimeSliderFlowDirection}
          onDateChange={handleTimeSliderDateChange}
          onHorizonChange={setSelectedHorizonDays}
          onPshWellsChange={handleTimeSliderPshWells}
          bundledTimeline={bundledTimeline}
          bundledSnapshots={bundledSnapshots}
          compounds={undefined}
        />
      )}

      {/* Remediation Impact Calculator — sits under the time slider, in the
          persistent area below the MCL legend. Shares the same
          `remediation_systems` table with CostAnalysisTab's panel so edits
          flow both ways. baselinePshLbs comes from the three-plume model's
          initial PSH mass — without it the calculator's "lbs removed" math
          would multiply against zero and report nothing removed. */}
      {showBars && siteId && (
        <RemediationImpactCalculator
          siteId={siteId}
          baselinePshLbs={threePlumeResult?.initial_psh_mass_lbs}
          baselineDissolvedMg={
            // Synthesize a dissolved-mass baseline from peak concentration ×
            // plume volume so the calculator's dissolved sparkline has scale
            // to multiply removal fractions against. Rough but defensible:
            //   mass_mg = peak_conc_ug_L × volume_L / 1000
            //   volume_L = area_sqft × thickness_ft × 28.3168 (cuft → L)
            threePlumeResult?.peak_dissolved_conc_ug_l != null
              && threePlumeResult?.peak_dissolved_area_sqft != null
              ? Math.max(
                  0,
                  (threePlumeResult.peak_dissolved_conc_ug_l / 1000) *
                  threePlumeResult.peak_dissolved_area_sqft *
                  (aquiferThicknessFt ?? 10) *
                  28.3168,
                )
              : undefined
          }
        />
      )}

      {/* Well Management Table */}
      {(recoveryWells.length > 0 || mdpeWells.length > 0) && (
        <WellManagementTable
          recoveryWells={recoveryWells}
          mdpeWells={mdpeWells}
          onRecoveryWellToggle={handleRecoveryWellToggle}
          onMdpeWellToggle={handleMdpeWellToggle}
          onRecoveryWellDelete={handleRecoveryWellDelete}
          onMdpeWellDelete={handleMdpeWellDelete}
          currentMonth={selectedMonth}
        />
      )}

      {/* Multi-Phase Coupling Physics block moved to the SITE DATA tab.
          See <CouplingPhysicsCard /> in PolygonSiteModeling.tsx. */}

      {/* Time to MCL info — Estimated PSH Depletion card removed per UX request */}
      {threePlumeResult && threePlumeResult.time_to_mcl_months && (
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
          <div className="text-xs text-mono-400 uppercase font-semibold mb-1">
            Time to MCL Attainment
          </div>
          <div className="text-2xl font-bold text-mono-100">
            {threePlumeResult.time_to_mcl_months} months
          </div>
          <p className="text-xs text-mono-500 mt-1">
            Estimated time for dissolved concentrations to reach Maximum Contaminant Level
          </p>
        </div>
      )}

      {/* Well Input Modal */}
      <WellInputModal
        isOpen={showWellModal}
        wellType={wellPlacementMode || 'recovery'}
        onClose={() => {
          setShowWellModal(false);
          setPendingWellLocation(null);
          setWellPlacementMode(null);
        }}
        onSave={handleWellSaved}
      />
    </div>
  );
}

export default ThreePlumeVisualization;
